import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import {
  deleteAllActions,
  saveConfig,
  updateSvgPartsMetaData,
} from "../../axios/index";
import loader from "../../loader.gif";
import "./index.css";

function SaveConfigPopup(props) {
  const dispatch = useDispatch();
  const partData = useSelector((state) => state.svgViewerPartData);
  const [saveOptions, setSaveOptions] = React.useState(props.modal.saveType);
  const [error, setError] = React.useState(false);
  const [statusType, setStatusType] = React.useState("draft");
  const svgSource = useSelector((state) => state.svgViewerData);
  const UploadOptions = useSelector((state) => state?.UploadFieldsOptions);

  const defaultScaleOptions = [
    { id: 1, scaleName: "Inch" },
    { id: 2, scaleName: "Millimeter" },
    { id: 3, scaleName: "Centimeter" },
  ];

  const handleScale = (e) => {
    props.setSelectedScale(Number(e.target.value));
  };

  const updateConfigMetaData = (key, val) => {
    dispatch(updateSvgPartsMetaData({ key, val }));
  };

  const calculateMidPointAndRange = (allCoordinates) => {
    function flattenArray(arr) {
      return arr.reduce((acc, val) => {
        return acc.concat(Array.isArray(val) ? flattenArray(val) : val);
      }, []);
    }

    const flattenedArray = flattenArray(allCoordinates || []);
    const groupedCoordinates = [];

    for (let i = 0; i < flattenedArray.length; i += 2) {
      const x = flattenedArray[i];
      const y = flattenedArray[i + 1];
      groupedCoordinates.push([x, y]);
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    groupedCoordinates?.forEach((coordSet) => {
      const [x, y] = coordSet;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });

    return {
      midpoint: [(minX + maxX) / 2, (minY + maxY) / 2],
      ranges: {
        x: {
          min: minX,
          max: maxX,
        },
        y: {
          min: minY,
          max: maxY,
        },
      },
    };
  };

  const convertCoordinates = (coordinates, fromScale, toScale) => {
    if (!Array.isArray(coordinates)) {
      return coordinates;
    }

    return coordinates?.map((coord) => {
      if (Array.isArray(coord)) {
        return convertCoordinates(coord, fromScale, toScale);
      }

      return convertToSelectedScale(coord, fromScale, toScale);
    });
  };

  const convertToSelectedScale = (value, fromScale, toScale) => {
    let data = value;
    const sourceScale = Number(fromScale);
    const targetScale = Number(toScale);

    if (sourceScale === 1 && targetScale === 2) {
      data *= 25.4;
    } else if (sourceScale === 1 && targetScale === 3) {
      data *= 2.54;
    } else if (sourceScale === 2 && targetScale === 1) {
      data /= 25.4;
    } else if (sourceScale === 2 && targetScale === 3) {
      data /= 10;
    } else if (sourceScale === 3 && targetScale === 1) {
      data /= 2.54;
    } else if (sourceScale === 3 && targetScale === 2) {
      data *= 10;
    }

    return data;
  };

  const convertAreaToSelectedScale = (value, fromScale, toScale) => {
    let data = value;
    const sourceScale = Number(fromScale);
    const targetScale = Number(toScale);

    if (sourceScale === 1 && targetScale === 2) {
      data *= 25.4 * 25.4;
    } else if (sourceScale === 1 && targetScale === 3) {
      data *= 2.54 * 2.54;
    } else if (sourceScale === 2 && targetScale === 1) {
      data /= 25.4 * 25.4;
    } else if (sourceScale === 2 && targetScale === 3) {
      data /= 10 * 10;
    } else if (sourceScale === 3 && targetScale === 1) {
      data /= 2.54 * 2.54;
    } else if (sourceScale === 3 && targetScale === 2) {
      data *= 10 * 10;
    }

    return data;
  };

  const getConvertedTransformedSVGPath = (coordinates) => {
    let newPath = "";
    const flatArray = coordinates?.flat?.(Infinity) || [];

    coordinates?.forEach((piece) => {
      newPath += "M";

      piece?.forEach((cords) => {
        newPath += ` ${cords[0]} ${cords[1]}`;
      });

      if (
        piece.length > 0 &&
        piece[0][0] === piece[piece.length - 1][0] &&
        piece[0][1] === piece[piece.length - 1][1]
      ) {
        newPath += "Z";
      }
    });

    if (
      flatArray[0] === flatArray[flatArray.length - 2] &&
      flatArray[1] === flatArray[flatArray.length - 1]
    ) {
      newPath += "Z";
    }

    return newPath;
  };

  const getScaleIdFromUnit = (unit) => {
    const normalizedUnit = String(unit || "").toLowerCase();

    if (
      normalizedUnit === "inch" ||
      normalizedUnit === "in" ||
      normalizedUnit === "1"
    ) {
      return 1;
    }

    if (normalizedUnit === "mm" || normalizedUnit === "2") {
      return 2;
    }

    if (normalizedUnit === "cm" || normalizedUnit === "3") {
      return 3;
    }

    return null;
  };

  const getUnitFromScaleId = (scale) => {
    if (Number(scale) === 1) {
      return "inch";
    }

    if (Number(scale) === 2) {
      return "mm";
    }

    if (Number(scale) === 3) {
      return "cm";
    }

    return "cm";
  };

  const getCurrentScale = (tempParts) => {
    if (
      props.moduleName === "assembly-viewer" &&
      !tempParts.data.configData.metaData.configId
    ) {
      return Number(
        svgSource?.data?.assemblyData?.metaData?.containerMetaData?.cadScale ||
          props.selectedScale
      );
    }

    if (
      props.moduleName === "config-viewer" ||
      tempParts.data.configData.metaData.configId
    ) {
      return (
        getScaleIdFromUnit(tempParts.data.configData.metaData.configUnits) ||
        Number(props.selectedScale)
      );
    }

    return Number(props.selectedScale);
  };

  const updateConfigUnitByScale = (tempParts, selectedScale) => {
    tempParts.data.configData.metaData.configUnits =
      getUnitFromScaleId(selectedScale);
  };

  const convertHoleScale = (
    hole,
    scale,
    selectedScale,
    newOriginX,
    newOriginY
  ) => {
    hole.partCalculatedOrigin = { x: newOriginX, y: newOriginY };

    hole.partArea = convertAreaToSelectedScale(
      Number.parseFloat(hole.partArea),
      scale,
      selectedScale
    );

    hole.partZ = convertToSelectedScale(
      Number.parseFloat(hole.partZ),
      scale,
      selectedScale
    );

    hole.partTransformedCoordinates = convertCoordinates(
      hole.partTransformedCoordinates,
      scale,
      selectedScale
    );

    hole.partTransformedSvgPath = getConvertedTransformedSVGPath(
      hole.partTransformedCoordinates
    );
  };

  const convertPartScale = (part, scale, selectedScale) => {
    const newOriginX = convertToSelectedScale(
      part.data.partCalculatedOrigin.x,
      scale,
      selectedScale
    );

    const newOriginY = convertToSelectedScale(
      part.data.partCalculatedOrigin.y,
      scale,
      selectedScale
    );

    part.data.partCalculatedOrigin = {
      x: newOriginX,
      y: newOriginY,
    };

    part.data.partArea = convertAreaToSelectedScale(
      Number.parseFloat(part.data.partArea),
      scale,
      selectedScale
    );

    part.data.partZ = convertToSelectedScale(
      Number.parseFloat(part.data.partZ),
      scale,
      selectedScale
    );

    part.data.partTransformedCoordinates = convertCoordinates(
      part.data.partTransformedCoordinates,
      scale,
      selectedScale
    );

    part.data.partTransformedSvgPath = getConvertedTransformedSVGPath(
      part.data.partTransformedCoordinates
    );

    part?.data?.partHoleReference?.forEach((hole) => {
      convertHoleScale(hole, scale, selectedScale, newOriginX, newOriginY);
    });
  };

  const applyScaleConversion = (tempParts) => {
    const scale = getCurrentScale(tempParts);
    const selectedScale = Number(props.selectedScale);

    updateConfigUnitByScale(tempParts, selectedScale);

    tempParts?.data?.configData?.partData?.forEach((part) => {
      convertPartScale(part, scale, selectedScale);
    });
  };

  const updateConfigRangeMetaData = (tempParts) => {
    const allTransformedCoordinates =
      tempParts?.data?.configData?.partData?.map(
        (part) => part.data.partTransformedCoordinates
      );

    const { midpoint, ranges } =
      calculateMidPointAndRange(allTransformedCoordinates);

    tempParts.data.configData.metaData.midpoint = midpoint;
    tempParts.data.configData.metaData.ranges = ranges;
  };

  const canSaveConfig = (tempParts) => {
    return (
      tempParts.data.configData.metaData.assemblyID !== "" &&
      tempParts.data.configData.metaData.configName !== ""
    );
  };

  const prepareConfigForSave = (tempParts) => {
    if (saveOptions === "new") {
      tempParts.data.configData.metaData.configId = "";
      tempParts.data.configData.type = "new";
    } else if (saveOptions === "update") {
      tempParts.data.configData.type = "existing";
    }

    tempParts.data.configData.metaData.configStatus = statusType;
  };

  const handleSaveEvent = () => {
    const tempParts = cloneDeep(partData);

    if (!props.editMode) {
      applyScaleConversion(tempParts);
    }

    updateConfigRangeMetaData(tempParts);

    if (canSaveConfig(tempParts)) {
      prepareConfigForSave(tempParts);

      const selectedScale = Number(props.selectedScale);
      tempParts.data.configData.metaData.configUnits =
        getUnitFromScaleId(selectedScale);
      props.setSelectedScale(selectedScale);

      dispatch(saveConfig({ configData: tempParts.data.configData }));
    } else {
      setError(true);
    }

    dispatch(deleteAllActions({}));
  };

  const renderLoader = () => {
    if (partData.loading === true) {
      return (
        <div className="fixedLoader">
          <img src={loader} className="loaderr" alt="" />
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (props.moduleName === "assembly-viewer") {
      const cadScale =
        svgSource?.data?.assemblyData?.metaData?.containerMetaData?.cadScale;

      if (cadScale) {
        props.setSelectedScale(Number(cadScale));
      }
    }
  }, [
    props.moduleName,
    svgSource?.data?.assemblyData?.metaData?.containerMetaData?.cadScale,
  ]);

  const getCurrentModuleEditStatus = () => {
    if (partData.data.configData?.type === "new") {
      return "draft";
    }

    return partData.data.configData.metaData?.configStatus || "";
  };

  const renderSaveOptions = () => {
    const currentModuleeditStatus = getCurrentModuleEditStatus();
    const configId = partData.data.configData.metaData?.configId;

    return (
      <div className="col-6 pr-1">
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name="saveOptions"
            id="radio1"
            value="new"
            checked={saveOptions === "new"}
            onChange={(e) => setSaveOptions(e.target.value)}
          />
          <label className="form-check-label" htmlFor="radio1">
            Save as New <span>{props.modal.module}</span>
          </label>
        </div>

        {configId !== "" && currentModuleeditStatus === "draft" ? (
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="saveOptions"
              id="radio2"
              value="update"
              checked={saveOptions === "update"}
              onChange={(e) => setSaveOptions(e.target.value)}
            />
            <label className="form-check-label" htmlFor="radio2">
              Update current <span>{props.modal.module}</span> Version
            </label>
          </div>
        ) : null}
      </div>
    );
  };

  const renderStatusOptions = () => {
    return (
      <div className="col-6 pr-1">
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name="statusType"
            id="radio3"
            value="inProduction"
            checked={statusType === "inProduction"}
            onChange={(e) => setStatusType(e.target.value)}
          />
          <label className="form-check-label" htmlFor="radio3">
            Publish this{" "}
            <span>
              {props.modal.module} to <b>Production</b>
            </span>
          </label>
        </div>

        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            name="statusType"
            id="radio4"
            value="draft"
            checked={statusType === "draft"}
            onChange={(e) => setStatusType(e.target.value)}
          />
          <label className="form-check-label" htmlFor="radio4">
            Save this{" "}
            <span>
              {props.modal.module} to <b>Drafts</b>
            </span>
          </label>
        </div>
      </div>
    );
  };

  const renderAssemblyDetails = () => {
    if (props.moduleName !== "assembly-viewer") {
      return <></>;
    }

    return (
      <div>
        <hr />
        <p>Please enter below details to save configuration.</p>

        <div>
          <label htmlFor="configName">Configuration Name</label>
          <input
            id="configName"
            type="text"
            value={partData?.data?.configData?.metaData?.configName || ""}
            className="form-control edit-input"
            onChange={(e) => {
              updateConfigMetaData("configName", e.target.value);
            }}
          />
        </div>

        <div>
          <label htmlFor="configDescription">Description</label>
          <input
            id="configDescription"
            type="text"
            value={
              partData?.data?.configData?.metaData?.configDescription || ""
            }
            className="form-control edit-input"
            onChange={(e) => {
              updateConfigMetaData("configDescription", e.target.value);
            }}
          />
        </div>

        <div>
          <div>Configuration Scale</div>
          <div className="dropdown">
            <select
              className="form-control edit-input"
              id="cadScale"
              key="cadScale"
              value={props.selectedScale}
              onChange={handleScale}
            >
              {(UploadOptions?.data?.scaleOptions || defaultScaleOptions).map(
                (value) => (
                  <option key={value?.id} value={value?.id}>
                    {value?.scaleName}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderEditableBody = () => {
    return (
      <div className="card-body">
        <p>{props.modal.info}</p>

        <form>
          <div className="row">
            {renderSaveOptions()}
            {renderStatusOptions()}
          </div>

          {renderAssemblyDetails()}
        </form>
      </div>
    );
  };

  const renderFailedBody = () => {
    return (
      <div className="card-body">
        <div className="failedMsg">
          <div className="failed">
            <i className="now-ui-icons ui-1_simple-remove"></i>&nbsp;{" "}
            {partData.responseType}
          </div>
          <div className="failedTxt">{partData.responseDesc}</div>
        </div>
      </div>
    );
  };

  const renderSuccessBody = () => {
    return (
      <div className="card-body">
        <div className="failedMsg">
          <div className="successMsg">
            <i className="now-ui-icons ui-1_check"></i>&nbsp;{" "}
            {partData.responseType}
          </div>
          <div className="successTxt">{partData.responseDesc}</div>
        </div>
      </div>
    );
  };

  const renderResponseBody = () => {
    if (partData.responseType === "Failed") {
      return renderFailedBody();
    }

    if (partData.responseType === "success") {
      return renderSuccessBody();
    }

    return null;
  };

  const renderCardBody = () => {
    if (partData.loading) {
      return <div className="card-body">{renderLoader()}</div>;
    }

    if (partData.responseType === "") {
      return renderEditableBody();
    }

    return renderResponseBody();
  };

  const renderFooterBtn = () => {
    if (partData.responseType === "" && partData.loading === false) {
      return (
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={props.closeModal}
          >
            <i className="now-ui-icons ui-1_simple-remove"></i> &nbsp;Cancel
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveEvent}
          >
            <i className="now-ui-icons arrows-1_cloud-upload-94"></i>{" "}
            &nbsp;Continue
          </button>
        </>
      );
    }

    if (partData.responseType === "Failed" && partData.loading === false) {
      return (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={props.closeModal}
        >
          <i className="now-ui-icons ui-1_simple-remove"></i> &nbsp;Close
        </button>
      );
    }

    if (partData.responseType === "success" && partData.loading === false) {
      return (
        <button
          type="button"
          className="btn btn-primary"
          onClick={props.closeModal}
        >
          &nbsp;Ok
        </button>
      );
    }

    if (partData.loading === true) {
      return (
        <>
          <button type="button" className="btn btn-secondary" disabled>
            <i className="now-ui-icons ui-1_simple-remove"></i> &nbsp;Cancel
          </button>

          <button type="button" className="btn btn-primary" disabled>
            <i className="now-ui-icons arrows-1_cloud-upload-94"></i>{" "}
            &nbsp;Continue
          </button>
        </>
      );
    }

    return null;
  };

  return (
    <dialog
      open
      className="modal fade show"
      id="exampleModal2"
      aria-labelledby="exampleModalLabel"
      style={{ display: "block" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="exampleModalLabel">
              <i className={props.modal.icon}></i> {props.modal.title}
            </h5>

            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={props.closeModal}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="card downloadFile" style={{ boxShadow: "none" }}>
              {renderCardBody()}
            </div>

            {error && (
              <p
                style={{
                  color: "red",
                  background: "#f0eeee",
                  padding: "10px",
                }}
              >
                Configuration file name is mandatory!
                <br />
              </p>
            )}
          </div>

          <div className="modal-footer">{renderFooterBtn()}</div>
        </div>
      </div>
    </dialog>
  );
}

SaveConfigPopup.propTypes = {
  moduleName: PropTypes.string,
  selectedScale: PropTypes.number,
  setSelectedScale: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  modal: PropTypes.shape({
    saveType: PropTypes.string,
    info: PropTypes.string,
    module: PropTypes.string,
    icon: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  closeModal: PropTypes.func,
};

export default SaveConfigPopup;